import { Breadcrumb } from '~/jobiqo/components/Breadcrumbs';
import {
  createSelectedFacetBreadcrumb,
  getBreadcrumbPath,
  INDUSTRY_FACET,
  OCCUPATIONAL_FIELD_FACET,
  SelectedFacetBreadcrumb,
} from '~/jobiqo/components/Breadcrumbs/utils';
import { Job, Term } from '~/jobiqo/types/types';

interface JobDetailBreadcrumbItemsParams {
  /**
   * Job detail entity.
   */
  job: Job;
  /**
   * Jobs listing page path.
   */
  jobsPage: string;
  /**
   * Localized jobs label.
   */
  jobsLabel: string;
}

/**
 * Maps a taxonomy term into a breadcrumb facet selection.
 *
 * @param {Term | null | undefined} term
 * @param {string} facetName
 * @returns
 *  Selected facet breadcrumb object or null when term is invalid.
 */
function getSelectedFacetFromTerm(
  term: Term | null | undefined,
  facetName: string,
): SelectedFacetBreadcrumb | null {
  if (!term?.label || !term?.tid) {
    return null;
  }

  return createSelectedFacetBreadcrumb({
    name: facetName,
    label: term.label,
    filter: term.tid.toString(),
    alias: term.urlAlias ?? undefined,
  });
}

/**
 * Builds all breadcrumb items for a job detail page.
 *
 * @param {JobDetailBreadcrumbItemsParams} params
 * @returns
 *  Breadcrumb items in this order: Jobs > Industry > Occupation > Job title.
 *  Missing terms are skipped gracefully.
 */
export function getJobDetailBreadcrumbItems({
  job,
  jobsPage,
  jobsLabel,
}: JobDetailBreadcrumbItemsParams): Breadcrumb[] {
  const items: Breadcrumb[] = [
    {
      label: jobsLabel,
      path: jobsPage,
    },
  ];

  const selectedFacets: SelectedFacetBreadcrumb[] = [];

  const selectedIndustry = getSelectedFacetFromTerm(
    job.organizationProfile?.industry,
    INDUSTRY_FACET,
  );

  if (selectedIndustry) {
    selectedFacets.push(selectedIndustry);
    items.push({
      label: selectedIndustry.label,
      path: getBreadcrumbPath(jobsPage, selectedFacets),
    });
  }

  const selectedOccupationalField = getSelectedFacetFromTerm(
    job.occupationalField?.find((term) => !!term?.label),
    OCCUPATIONAL_FIELD_FACET,
  );

  if (selectedOccupationalField) {
    selectedFacets.push(selectedOccupationalField);
    items.push({
      label: selectedOccupationalField.label,
      path: getBreadcrumbPath(jobsPage, selectedFacets),
    });
  }

  if (job.title && job.url?.path) {
    items.push({ label: job.title, path: job.url.path });
  }

  return items;
}
